import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCurrentRole, type AppRole } from "@/hooks/useCurrentRole";

const DEFAULT_ROUTE_BY_ROLE: Record<AppRole, string> = {
  admin: "/time-entry",
  project_manager: "/time-entry",
  site_manager: "/time-entry",
  employee: "/time-entry",
  owner: "/owner",
};

interface RoleRouteProps {
  allowedRoles: AppRole[];
  children: React.ReactNode;
  preload?: () => Promise<unknown>;
}

export function RoleRoute({ allowedRoles, children, preload }: RoleRouteProps) {
  const { loading, isAuthenticated, role, accountRole } = useCurrentRole();
  const location = useLocation();

  useEffect(() => {
    void preload?.();
  }, [preload]);

  if (loading) {
    return <ProtectedRouteFallback pathname={location.pathname} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!accountRole) {
    return <Navigate to="/login" replace state={{ from: location, reason: "missing-role" }} />;
  }

  if (allowedRoles.includes(role)) {
    return <>{children}</>;
  }

  return <Navigate to={DEFAULT_ROUTE_BY_ROLE[role]} replace state={{ from: location }} />;
}

function ProtectedRouteFallback({ pathname }: { pathname: string }) {
  if (pathname === "/dashboard") {
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-background">
        <main className="min-w-0 flex-1 pt-16 lg:ml-[240px] lg:pt-0">
          <div className="mx-auto w-full max-w-7xl min-w-0 p-3 sm:p-4 md:p-6 lg:p-8">
            <div className="mb-8">
              <div className="h-9 w-32 rounded bg-muted" />
              <div className="mt-2 h-4 w-64 max-w-full rounded bg-muted" />
            </div>

            <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-xl border bg-card p-5">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="h-4 w-40 max-w-full rounded bg-muted" />
                      <div className="mt-3 h-8 w-24 rounded bg-muted" />
                      {index === 0 && <div className="mt-2 h-3 w-28 rounded bg-muted" />}
                    </div>
                    <div className="h-10 w-10 rounded-lg bg-muted" />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-xl border bg-card p-4 md:p-6 lg:col-span-2">
                <div className="mb-4 h-6 w-48 rounded bg-muted" />
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="rounded-xl border bg-muted/20 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="h-4 w-40 rounded bg-muted" />
                        <div className="h-4 w-4 rounded bg-muted" />
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                        <div className="h-3 rounded bg-muted" />
                        <div className="h-3 rounded bg-muted" />
                        <div className="h-3 rounded bg-muted" />
                      </div>
                      {index > 0 && <div className="mt-4 h-2 rounded-full bg-muted" />}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border bg-card p-4 md:p-6">
                <div className="mb-4 h-6 w-36 rounded bg-muted" />
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 border-b pb-3 last:border-0">
                      <div className="min-w-0 flex-1">
                        <div className="h-4 w-24 rounded bg-muted" />
                        <div className="mt-2 h-3 w-32 rounded bg-muted" />
                      </div>
                      <div className="h-4 w-20 rounded bg-muted" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (pathname === "/locked-weeks") {
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-background">
        <main className="min-w-0 flex-1 pt-16 lg:ml-[240px] lg:pt-0">
          <div className="mx-auto w-full max-w-7xl min-w-0 p-3 sm:p-4 md:p-6 lg:p-8">
            <div className="mb-6 md:mb-8">
              <div className="h-9 w-36 rounded bg-muted" />
              <div className="mt-2 h-4 w-[520px] max-w-full rounded bg-muted" />
            </div>

            <div className="mb-5 grid gap-3 md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="rounded-lg border bg-card p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 rounded bg-muted" />
                    <div>
                      <div className="h-3 w-20 rounded bg-muted" />
                      <div className="mt-2 h-6 w-10 rounded bg-muted" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="h-10 w-full rounded-md bg-muted md:w-72" />
              <div className="h-9 w-full rounded-md bg-muted md:w-28" />
            </div>

            <div className="mb-4 rounded-lg border bg-card p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="h-4 w-32 rounded bg-muted" />
                  <div className="mt-2 h-3 w-44 rounded bg-muted" />
                </div>
                <div className="flex gap-2">
                  <div className="h-9 w-9 rounded-md bg-muted" />
                  <div className="h-9 w-20 rounded-md bg-muted" />
                  <div className="h-9 w-9 rounded-md bg-muted" />
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-card">
              <div className="border-b p-6 pb-3">
                <div className="h-5 w-48 rounded bg-muted" />
                <div className="mt-2 h-4 w-72 max-w-full rounded bg-muted" />
              </div>
              <div className="p-4">
                <div className="mb-4 h-10 w-full max-w-sm rounded bg-muted" />
                <div className="space-y-3 md:hidden">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="rounded-lg border bg-background p-4">
                      <div className="h-4 w-36 rounded bg-muted" />
                      <div className="mt-2 h-3 w-48 max-w-full rounded bg-muted" />
                      <div className="mt-4 h-8 rounded bg-muted" />
                    </div>
                  ))}
                </div>
                <div className="hidden space-y-3 md:block">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="grid grid-cols-[1fr_80px_80px_80px_120px] gap-4 border-b pb-3 last:border-0">
                      <div>
                        <div className="h-4 w-36 rounded bg-muted" />
                        <div className="mt-2 h-3 w-24 rounded bg-muted" />
                      </div>
                      <div className="h-4 rounded bg-muted" />
                      <div className="h-4 rounded bg-muted" />
                      <div className="h-4 rounded bg-muted" />
                      <div className="h-8 rounded bg-muted" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (pathname === "/payroll") {
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-background">
        <main className="min-w-0 flex-1 pt-16 lg:ml-[240px] lg:pt-0">
          <div className="mx-auto w-full max-w-7xl min-w-0 p-3 sm:p-4 md:p-6 lg:p-8">
            <div className="mb-6 md:mb-8">
              <div className="h-9 w-24 rounded bg-muted" />
              <div className="mt-2 h-4 w-[460px] max-w-full rounded bg-muted" />
            </div>

            <div className="mb-4 flex items-center justify-between gap-3 md:mb-6">
              <div className="h-9 w-20 rounded-md bg-muted" />
              <div className="h-5 w-36 rounded bg-muted" />
              <div className="h-9 w-20 rounded-md bg-muted" />
            </div>

            <div className="space-y-3 lg:hidden">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-xl border bg-card p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="h-4 w-32 rounded bg-muted" />
                    <div className="h-5 w-12 rounded bg-muted" />
                  </div>
                  <div className="mt-4 space-y-3">
                    {Array.from({ length: 2 }).map((__, weekIndex) => (
                      <div key={weekIndex}>
                        <div className="mb-2 h-3 w-14 rounded bg-muted" />
                        <div className="grid grid-cols-7 gap-1">
                          {Array.from({ length: 7 }).map((___, dayIndex) => (
                            <div key={dayIndex} className="h-10 rounded-md bg-muted" />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden overflow-hidden rounded-xl border bg-card lg:block">
              <div className="grid grid-cols-[160px_repeat(14,minmax(48px,1fr))_80px] border-b bg-muted/50 p-3">
                {Array.from({ length: 16 }).map((_, index) => (
                  <div key={index} className="mx-1 h-4 rounded bg-muted" />
                ))}
              </div>
              {Array.from({ length: 6 }).map((_, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-[160px_repeat(14,minmax(48px,1fr))_80px] border-b p-3 last:border-0">
                  {Array.from({ length: 16 }).map((_, colIndex) => (
                    <div key={colIndex} className="mx-1 h-4 rounded bg-muted" />
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="rounded-xl border bg-card p-3 sm:p-5">
                  <div className="h-3 w-20 rounded bg-muted" />
                  <div className="mt-2 h-7 w-14 rounded bg-muted" />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (pathname === "/invoicing") {
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-background">
        <main className="min-w-0 flex-1 pt-16 lg:ml-[240px] lg:pt-0">
          <div className="mx-auto w-full max-w-7xl min-w-0 p-3 sm:p-4 md:p-6 lg:p-8">
            <div className="mb-6 md:mb-8">
              <div className="h-9 w-32 rounded bg-muted" />
              <div className="mt-2 h-4 w-[500px] max-w-full rounded bg-muted" />
            </div>

            <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="rounded-xl border bg-card p-4">
                  <div className="h-3 w-28 rounded bg-muted" />
                  <div className="mt-3 h-7 w-24 rounded bg-muted" />
                </div>
              ))}
            </div>

            <div className="mb-6 rounded-xl border bg-card p-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="h-4 w-40 rounded bg-muted" />
                <div className="h-4 w-24 rounded bg-muted" />
              </div>
              <div className="h-3 rounded-full bg-muted" />
            </div>

            <div className="mb-6 rounded-xl border bg-card p-5">
              <div className="mb-4 h-4 w-36 rounded bg-muted" />
              <div className="space-y-3 md:hidden">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="rounded-lg border p-3">
                    <div className="h-4 w-40 rounded bg-muted" />
                    <div className="mt-2 h-3 w-24 rounded bg-muted" />
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      <div className="h-10 rounded bg-muted" />
                      <div className="h-10 rounded bg-muted" />
                      <div className="h-10 rounded bg-muted" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden md:block">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="grid grid-cols-[1fr_130px_130px_130px_120px] gap-3 border-t py-3">
                    <div>
                      <div className="h-4 w-40 rounded bg-muted" />
                      <div className="mt-2 h-3 w-24 rounded bg-muted" />
                    </div>
                    <div className="h-4 rounded bg-muted" />
                    <div className="h-4 rounded bg-muted" />
                    <div className="h-4 rounded bg-muted" />
                    <div className="h-8 rounded bg-muted" />
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border bg-card">
              <div className="border-b px-5 py-3">
                <div className="h-4 w-28 rounded bg-muted" />
              </div>
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="grid grid-cols-[120px_1fr_100px] gap-3 border-t px-5 py-3">
                  <div className="h-4 rounded bg-muted" />
                  <div className="h-4 rounded bg-muted" />
                  <div className="h-4 rounded bg-muted" />
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (pathname === "/settings") {
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-background">
        <main className="min-w-0 flex-1 pt-16 lg:ml-[240px] lg:pt-0">
          <div className="mx-auto w-full max-w-7xl min-w-0 p-3 sm:p-4 md:p-6 lg:p-8">
            <div className="mb-6 md:mb-8">
              <div className="h-9 w-36 rounded bg-muted" />
              <div className="mt-2 h-4 w-[460px] max-w-full rounded bg-muted" />
            </div>

            <div className="grid max-w-2xl gap-6">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="rounded-lg border bg-card p-6">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded bg-muted" />
                    <div className="h-5 w-36 rounded bg-muted" />
                  </div>
                  <div className="mt-2 h-4 w-64 max-w-full rounded bg-muted" />
                  <div className="mt-6 space-y-3">
                    <div className="h-11 rounded bg-muted" />
                    {index === 1 && (
                      <>
                        <div className="h-11 rounded bg-muted" />
                        <div className="h-11 rounded bg-muted" />
                      </>
                    )}
                    {index === 2 && (
                      <div className="flex justify-end">
                        <div className="h-10 w-44 rounded bg-muted" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (pathname === "/time-entry") {
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-background">
        <main className="min-w-0 flex-1 pt-16 lg:ml-[240px] lg:pt-0">
          <div className="mx-auto w-full max-w-7xl min-w-0 p-3 sm:p-4 md:p-6 lg:p-8">
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Timeregistrering</h1>
              <p className="mt-1 text-sm text-muted-foreground">Indlaeser ugeoversigt</p>
            </div>

            <div className="space-y-4 animate-pulse">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="grid w-full grid-cols-3 gap-1 rounded-xl bg-muted p-1 sm:w-[360px]">
                  <div className="h-9 rounded-lg bg-background/70" />
                  <div className="h-9 rounded-lg bg-background/70" />
                  <div className="h-9 rounded-lg bg-background/70" />
                </div>
                <div className="h-9 w-24 rounded-xl bg-muted" />
              </div>

              <div className="grid grid-cols-[44px_1fr_44px] items-center gap-2">
                <div className="h-10 rounded-xl bg-muted" />
                <div className="mx-auto h-5 w-52 max-w-full rounded bg-muted" />
                <div className="h-10 rounded-xl bg-muted" />
              </div>

              <div className="overflow-hidden rounded-lg border bg-card">
                <div className="grid border-b" style={{ gridTemplateColumns: "44px repeat(7,minmax(0,1fr))" }}>
                  <div className="h-16 border-r bg-muted/40" />
                  {Array.from({ length: 7 }).map((_, index) => (
                    <div key={index} className="h-16 border-r bg-muted/40 last:border-r-0" />
                  ))}
                </div>
                <div className="grid h-[58vh] min-h-[420px]" style={{ gridTemplateColumns: "44px repeat(7,minmax(0,1fr))" }}>
                  <div className="border-r bg-muted/20" />
                  {Array.from({ length: 7 }).map((_, index) => (
                    <div key={index} className="border-r bg-background last:border-r-0">
                      <div className="mt-20 mx-1 h-16 rounded-md border bg-muted/50" />
                      {index === 2 && <div className="mx-1 mt-12 h-20 rounded-md border bg-muted/50" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (pathname === "/material-library") {
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-background">
        <main className="min-w-0 flex-1 pt-16 lg:ml-[240px] lg:pt-0">
          <div className="mx-auto w-full max-w-7xl min-w-0 p-3 sm:p-4 md:p-6 lg:p-8">
            <div className="mb-5 flex flex-col gap-3 md:mb-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="h-8 w-44 rounded bg-muted" />
                  <div className="mt-2 h-4 w-28 rounded bg-muted" />
                </div>
                <div className="h-11 w-full rounded-md bg-muted sm:h-10 sm:w-36" />
              </div>
            </div>

            <div className="mb-5 rounded-xl border border-border/60 bg-card p-3 shadow-sm sm:p-4">
              <div className="h-11 rounded-md bg-muted sm:h-10" />
              <div className="mt-3 flex flex-wrap gap-1.5">
                {Array.from({ length: 7 }).map((_, index) => (
                  <div key={index} className="h-8 w-20 rounded-full bg-muted sm:h-7" />
                ))}
              </div>
            </div>

            <div className="space-y-3 md:hidden">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="h-[178px] rounded-xl border bg-card p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="h-4 w-44 rounded bg-muted" />
                      <div className="mt-2 h-5 w-24 rounded bg-muted" />
                    </div>
                    <div className="h-10 w-20 rounded bg-muted" />
                  </div>
                  <div className="mt-4 h-[58px] rounded-lg bg-muted/70" />
                  <div className="mt-3 flex gap-1">
                    <div className="h-5 w-14 rounded bg-muted" />
                    <div className="h-5 w-16 rounded bg-muted" />
                    <div className="h-5 w-12 rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden h-[calc(100vh-320px)] min-h-[300px] rounded-xl border border-border/60 bg-card md:block" />
          </div>
        </main>
      </div>
    );
  }

  return <div className="min-h-screen bg-background" />;
}

export function HomeRedirect() {
  const { loading, isAuthenticated, accountRole, role } = useCurrentRole();

  if (loading) {
    return <div className="min-h-screen bg-background" />;
  }

  if (!isAuthenticated || !accountRole) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={DEFAULT_ROUTE_BY_ROLE[role]} replace />;
}
